import { AUTH_URL } from './config.js';

/**
 * The never-throws request plumbing behind every function in `account.ts`:
 * URL building, cookie credentials, status → error mapping, and body
 * decoding. Split out because `account.ts` mirrors `@bn-games/social-client`'s
 * `SocialResult<T>` convention (see that package's `http.ts`) without
 * importing it — `social-client` already depends on `auth-client`, so the
 * reverse import would be circular.
 */

export type AccountErrorKind =
  | 'unauthenticated'
  | 'bad_request'
  | 'not_found'
  | 'conflict'
  | 'network'
  | 'unknown';

export interface AccountError {
  kind: AccountErrorKind;
  /** HTTP status, or `0` when the request never reached the server. */
  status: number;
  /** The server's `{ error }` message when it sent one, else a fallback. */
  message: string;
}

export type AccountResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: AccountError };

export type Decoder<T> = (body: unknown) => T | null;

const NETWORK_STATUS = 0;
const STATUS_BAD_REQUEST = 400;
const STATUS_UNAUTHORIZED = 401;
const STATUS_NOT_FOUND = 404;
const STATUS_CONFLICT = 409;
const STATUS_OK_MIN = 200;
const STATUS_OK_MAX = 299;

const KIND_BY_STATUS: ReadonlyMap<number, AccountErrorKind> = new Map([
  [STATUS_BAD_REQUEST, 'bad_request'],
  [STATUS_UNAUTHORIZED, 'unauthenticated'],
  [STATUS_NOT_FOUND, 'not_found'],
  [STATUS_CONFLICT, 'conflict'],
]);

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function ok<T>(value: T): AccountResult<T> {
  return { ok: true, value };
}

export function fail<T>(error: AccountError): AccountResult<T> {
  return { ok: false, error };
}

/** Body for a JSON request — `Content-Type` header plus a serialized body. */
export function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

/**
 * The 401 body is plain text on some routes, and a proxy may substitute HTML
 * for any status, so an unreadable error body degrades to the fallback
 * rather than failing the whole call.
 */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && typeof body['error'] === 'string') {
      return body['error'];
    }
  } catch {
    // fall through
  }
  return fallback;
}

async function toError(response: Response): Promise<AccountError> {
  const kind = KIND_BY_STATUS.get(response.status) ?? 'unknown';
  const message = await errorMessage(response, 'request failed (' + response.status + ')');
  return { kind, status: response.status, message };
}

async function send(path: string, init: RequestInit): Promise<Response | null> {
  try {
    return await fetch(AUTH_URL + path, { ...init, credentials: 'include' });
  } catch {
    return null;
  }
}

/** For routes that answer with a JSON body on success (2xx). */
export async function requestJson<T>(
  path: string,
  init: RequestInit,
  decode: Decoder<T>,
): Promise<AccountResult<T>> {
  const response = await send(path, init);
  if (response === null) {
    return fail({ kind: 'network', status: NETWORK_STATUS, message: 'network error' });
  }
  if (response.status < STATUS_OK_MIN || response.status > STATUS_OK_MAX) {
    return fail(await toError(response));
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return fail({ kind: 'unknown', status: response.status, message: 'malformed response' });
  }

  const value = decode(body);
  if (value === null) {
    return fail({ kind: 'unknown', status: response.status, message: 'unexpected response' });
  }
  return ok(value);
}

/** For routes that answer `204 No Content` on success. */
export async function requestVoid(path: string, init: RequestInit): Promise<AccountResult<void>> {
  const response = await send(path, init);
  if (response === null) {
    return fail({ kind: 'network', status: NETWORK_STATUS, message: 'network error' });
  }
  if (response.status < STATUS_OK_MIN || response.status > STATUS_OK_MAX) {
    return fail(await toError(response));
  }
  return ok(undefined);
}
